import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { PrivacyLink } from "@/components/privacy/PrivacyLink";
import { SmsDisclosure } from "@/components/privacy/SmsDisclosure";

export default function EarlyAccessModal({ open, onOpenChange, source = "hero" }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    zip: "",
    dob: "",
    sms_opt_in: false,
    email_opt_in: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name) {
      toast.error("Name and email required.");
      return;
    }
    setBusy(true);
    try {
      await http.post("/early-access", { ...form, source });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sign you up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", zip: "", dob: "", sms_opt_in: false, email_opt_in: false });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="ph-no-capture ph-mask max-w-lg max-h-[90dvh] overflow-y-auto p-0 bg-[color:var(--bg)] border border-[color:var(--line)]" data-testid="early-access-modal">
        {!done ? (
          <>
            <DialogHeader className="px-8 pt-8 pb-4 border-b border-[color:var(--line)]">
              <div className="label !text-[10px] mb-3">Pre-Launch · By Invitation</div>
              <DialogTitle className="font-display text-3xl tracking-[-0.02em] leading-[1.05]">
                Join the early <span className="editorial normal-case">access list.</span>
              </DialogTitle>
              <DialogDescription className="text-sm text-[color:var(--ink-2)] mt-2">
                First batch is small. Reserve early access to launch pricing and the limited Charter Pack. Choose below if you’d like email or text updates.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="px-8 py-6 space-y-4" data-testid="early-access-form">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required testId="ea-firstname">
                  <input
                    required
                    type="text"
                    value={form.first_name}
                    onChange={(e) => update("first_name", e.target.value)}
                    className="w-full bg-transparent border-b border-[color:var(--line)] py-2 text-sm outline-none focus:border-[color:var(--ink)] transition"
                    data-testid="ea-firstname-input"
                  />
                </Field>
                <Field label="Last name" testId="ea-lastname">
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={(e) => update("last_name", e.target.value)}
                    className="w-full bg-transparent border-b border-[color:var(--line)] py-2 text-sm outline-none focus:border-[color:var(--ink)] transition"
                    data-testid="ea-lastname-input"
                  />
                </Field>
              </div>

              <Field label="Email" required testId="ea-email">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full bg-transparent border-b border-[color:var(--line)] py-2 text-sm outline-none focus:border-[color:var(--ink)] transition"
                  data-testid="ea-email-input"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" required={form.sms_opt_in} testId="ea-phone">
                  <input
                    type="tel"
                    required={form.sms_opt_in}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (555) 555-5555"
                    className="w-full bg-transparent border-b border-[color:var(--line)] py-2 text-sm outline-none focus:border-[color:var(--ink)] transition placeholder:text-[color:var(--ink-4)]"
                    data-testid="ea-phone-input"
                  />
                </Field>
                <Field label="Zip code" testId="ea-zip">
                  <input
                    type="text"
                    value={form.zip}
                    onChange={(e) => update("zip", e.target.value)}
                    inputMode="numeric"
                    maxLength={10}
                    className="w-full bg-transparent border-b border-[color:var(--line)] py-2 text-sm outline-none focus:border-[color:var(--ink)] transition"
                    data-testid="ea-zip-input"
                  />
                </Field>
              </div>

              <Field label="Date of birth" hint="Optional · for birthday drops" testId="ea-dob">
                <input
                  type="date"
                  value={form.dob}
                  onChange={(e) => update("dob", e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                  min="1900-01-01"
                  className="w-full bg-transparent border-b border-[color:var(--line)] py-2 text-sm outline-none focus:border-[color:var(--ink)] transition"
                  data-testid="ea-dob-input"
                />
              </Field>

              <div className="pt-2 space-y-2.5">
                <Checkbox
                  checked={form.email_opt_in}
                  onChange={(v) => update("email_opt_in", v)}
                  label="Email me drops, ingredient drops, and launch news."
                  testId="ea-email-optin"
                />
                <Checkbox
                  checked={form.sms_opt_in}
                  onChange={(v) => update("sms_opt_in", v)}
                  label="Text me Vitura launch news and offers (optional)."
                  testId="ea-sms-optin"
                  describedBy="ea-sms-disclosure"
                />
                <SmsDisclosure />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 mt-3 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.25em] hover:bg-[#2a2a2a] disabled:opacity-50 transition"
                data-testid="ea-submit"
              >
                {busy ? "Saving" : "Reserve my spot →"}
              </button>

              <p className="label !text-[9px] text-[color:var(--ink-3)] !tracking-[0.15em] !normal-case mt-2" data-testid="ea-privacy-notice">
                We use your details for early access and the communication preferences submitted here. Our <PrivacyLink testId="ea-form-privacy-link" newTab /> explains collection, service providers, and your choices.
              </p>
            </form>
          </>
        ) : (
          <div className="p-10 text-center" data-testid="ea-success">
            <div className="w-12 h-12 mx-auto rounded-full bg-[color:var(--accent-cm)]/30 flex items-center justify-center">
              <Check size={20} strokeWidth={2} />
            </div>
            <h3 className="font-display text-3xl mt-6 tracking-[-0.02em]">You're on the list.</h3>
            <p className="editorial text-xl mt-3 text-[color:var(--ink-2)]" data-testid="ea-success-preferences">Your early-access preferences are saved.</p>
            <p className="text-sm text-[color:var(--ink-2)] mt-6 max-w-sm mx-auto">
              {form.email_opt_in || form.sms_opt_in ? "We’ll send launch news through the channels you selected." : "You haven’t subscribed to marketing emails or texts."}
            </p>
            <button onClick={close} className="mt-8 label underline" data-testid="ea-success-close">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, hint, testId, children }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="label !text-[9.5px] mb-1.5 flex items-baseline gap-2">
        <span>{label}{required && <span className="ml-0.5 text-[color:var(--ink-3)]">*</span>}</span>
        {hint && <span className="!text-[8.5px] !tracking-[0.18em] text-[color:var(--ink-3)] !normal-case">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Checkbox({ checked, onChange, label, testId, describedBy }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} aria-describedby={describedBy}
        className="shrink-0 w-4 h-4 mt-0.5 accent-[#0f0f10]" data-testid={`${testId}-checkbox`} />
      <span className="text-[12px] leading-snug text-[color:var(--ink-2)]">{label}</span>
    </label>
  );
}
